import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import EditIcon from 'transfix-icons/build/EditIcon'
import { Card } from 'transfix-ui/components/Card'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { ENTITY_TYPES, ICompanyInfo } from 'types/carrier'
import { convertToString } from 'apps/carrier/constants/companyInfo'
import {
  getCompanyInfo,
  openCarrierInformationDialog,
} from 'apps/carrier/utils/actions/carrierInfo'
import { IStore } from 'apps/carrier/utils/reducers'
import { capitalize } from 'utils/capitalize'
import { DISPLAY } from 'constants/display'
import { CompanyInformationDialog } from './CompanyInformationDialog'

interface ISelector {
  id?: string
  info: ICompanyInfo
  loading: boolean
  error?: string
}

const convertEntityType = (type: keyof typeof ENTITY_TYPES | undefined) =>
  type ? ENTITY_TYPES[type] : DISPLAY.empty

export const DeprecatedCompanyInformation = () => {
  const dispatch = useDispatch()
  const { error, id, info, loading } = useSelector<IStore, ISelector>(
    ({ carrier, companyInfo }) => ({
      id: carrier.carrierDetails?.id,
      info: companyInfo.info,
      loading: companyInfo.loading,
      error: companyInfo.error,
    })
  )

  useEffect(() => {
    if (id) {
      dispatch(getCompanyInfo(id))
    }
  }, [id])

  const openDialogHandler = () => dispatch(openCarrierInformationDialog())
  const {
    dbaName,
    dotNumber,
    ein,
    entityType,
    isCarbCompliant,
    isDisadvantagedBusinessEntity,
    legalName,
    mcNumber,
  } = info
  const castedEntityType = entityType as keyof typeof ENTITY_TYPES | undefined
  return (
    <Card>
      <div className='row no-gutter pv4'>
        <h3 className='fl mb0 mt2'>Company Information</h3>
        <div className='fr'>
          <IconButton
            Icon={EditIcon}
            color='tertiary'
            onClick={openDialogHandler}
            data-test='edit-commodity'
            label='Edit'
            size='large'
          />
        </div>
      </div>
      {loading && <Spinner position='relative' />}
      {error && <InlineMessage message={error} type={INLINE_MESSAGE_VARIANTS.ALERT} />}
      {!loading && !error && (
        <>
          <div className='row no-gutter'>
            <div className='col-xs-4'>
              <small>Legal Name</small>
              <div>{legalName || DISPLAY.empty}</div>
            </div>
            <div className='col-xs-4'>
              <small>MC Number</small>
              <div>{mcNumber || DISPLAY.empty}</div>
            </div>
          </div>
          <div className='row no-gutter'>
            <div className='col-xs-4'>
              <small>DBA</small>
              <div>{dbaName || DISPLAY.empty}</div>
            </div>
            <div className='col-xs-4'>
              <small>DOT Number</small>
              <div>{dotNumber || DISPLAY.empty}</div>
            </div>
          </div>
          <div className='row no-gutter'>
            <div className='col-xs-4'>
              <small>Entity Type</small>
              <div>{convertEntityType(castedEntityType)}</div>
            </div>
            <div className='col-xs-4'>
              <small>EIN</small>
              <div>{ein || DISPLAY.empty}</div>
            </div>
          </div>
          <div className='row no-gutter'>
            <div className='col-xs-4'>
              <small>CARB Compliant?</small>
              <div>{capitalize(convertToString(isCarbCompliant)) || DISPLAY.empty}</div>
            </div>
            <div className='col-xs-4'>
              <small>D/M/WBE</small>
              <div>
                {capitalize(convertToString(isDisadvantagedBusinessEntity)) || DISPLAY.empty}
              </div>
            </div>
          </div>
        </>
      )}
      <CompanyInformationDialog />
    </Card>
  )
}
